/**
 * =====================================================================================
 * SERVICIO DE PRODUCTOS Y SERVICIOS - productosServicios.service.js
 * =====================================================================================
 *
 * Este servicio gestiona el catálogo de PRODUCTOS Y SERVICIOS que vende el emisor.
 * Estos son los ítems que pueden agregarse al cuerpo de una factura.
 *
 * ¿QUÉ ES UN PRODUCTO/SERVICIO?
 * -----------------------------
 * Es cada artículo o servicio que la empresa vende y puede facturar.
 *
 * EJEMPLOS DE PRODUCTOS (bienes físicos):
 * - Computadora laptop HP 15"
 * - Mouse inalámbrico
 * - Teclado USB
 *
 * EJEMPLOS DE SERVICIOS (intangibles):
 * - Servicio de mantenimiento de equipos
 * - Consultoría empresarial
 * - Desarrollo de software
 *
 * DATOS DE UN PRODUCTO/SERVICIO:
 * ------------------------------
 * - Código: Identificador interno (SKU)
 * - Nombre: Nombre corto
 * - Descripción: Descripción completa (aparece en factura)
 * - Precio de Venta: Precio unitario
 * - Unidad de Medida: Unidad, docena, kilogramo, etc. (catálogo CAT-014)
 * - Tipo de Ítem: Bien o Servicio (catálogo CAT-011)
 * - Activo: Si está disponible para facturar
 *
 * FLUJO EN LA EMISIÓN DE FACTURA:
 * -------------------------------
 *
 * 1. Usuario abre formulario de nueva factura
 * 2. En la sección "Cuerpo del documento" busca producto
 * 3. Sistema busca usando search() de este servicio
 * 4. Usuario selecciona producto
 * 5. Sistema autocompleta: descripción, precio, unidad
 * 6. Usuario ajusta cantidad
 * 7. Se calcula automáticamente: subtotal, IVA
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/views/configuracion/ProductosServiciosView.vue: CRUD de productos
 * - src/components/emision/CuerpoDocumento.vue: Agrega productos a la factura
 * - Backend: Controllers/ProductosServiciosController.cs
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE PRODUCTOS Y SERVICIOS
 * =====================================================================================
 *
 * Similar al servicio de receptores, incluye:
 * 1. Operaciones CRUD estándar
 * 2. Búsqueda rápida para autocomplete
 * 3. Mapeo de datos entre frontend y backend
 * 4. Activar/desactivar productos
 */
const productosServiciosService = {
  /**
   * ===========================================================================
   * MÉTODO: getAll
   * ===========================================================================
   *
   * Obtiene lista paginada de productos/servicios con búsqueda opcional.
   *
   * ENDPOINT: GET /api/ProductosServicios?page=1&pageSize=10&search=laptop
   *
   * @param {Object} params - Parámetros de consulta
   * @param {number} params.page - Número de página (1-indexed)
   * @param {number} params.pageSize - Cantidad por página
   * @param {string} params.search - Término de búsqueda opcional
   * @param {number} params.sucursalId - Filtrar por sucursal (opcional)
   * @returns {Promise<Object>} { items: [...], totalCount, page, pageSize }
   */
  async getAll(params = {}) {
    // Construir query string dinámicamente
    const queryParams = new URLSearchParams()

    if (params.page) queryParams.append('pageNumber', params.page)
    if (params.pageSize) queryParams.append('pageSize', params.pageSize)
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm)
    if (params.sucursalId) queryParams.append('sucursalId', params.sucursalId)
    if (params.categoriaId) queryParams.append('categoriaId', params.categoriaId)
    if (params.marcaId) queryParams.append('marcaId', params.marcaId)
    if (params.unidadMedida) queryParams.append('unidadMedida', params.unidadMedida)
    if (params.tipo) queryParams.append('tipo', params.tipo)
    if (params.orderBy) queryParams.append('orderBy', params.orderBy)
    if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection)

    const response = await api.get(`/ProductosServicios?${queryParams.toString()}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getById
   * ===========================================================================
   *
   * Obtiene un producto/servicio específico por su ID.
   *
   * ENDPOINT: GET /api/ProductosServicios/{id}
   *
   * @param {number} id - ID del producto/servicio
   * @returns {Promise<Object>} Datos completos del producto
   */
  async getById(id) {
    const response = await api.get(`/ProductosServicios/${id}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: search
   * ===========================================================================
   *
   * Busca productos/servicios por término de búsqueda.
   * Busca en: código, nombre, descripción.
   *
   * ENDPOINT: GET /api/ProductosServicios/search?searchTerm=xxx
   *
   * USO PRINCIPAL:
   * Autocomplete en CuerpoDocumento.vue cuando el usuario escribe
   * el nombre de un producto para agregarlo a la factura.
   *
   * @param {string} searchTerm - Término a buscar
   * @returns {Promise<Array>} Lista de productos que coinciden
   *
   * EJEMPLO:
   * search("laptop") → Busca productos con "laptop" en nombre o descripción
   * search("SKU001") → Busca por código exacto o parcial
   */
  async search(searchTerm) {
    const response = await api.get(`/ProductosServicios/search?searchTerm=${encodeURIComponent(searchTerm)}`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: create
   * ===========================================================================
   *
   * Crea un nuevo producto/servicio.
   *
   * ENDPOINT: POST /api/ProductosServicios
   *
   * @param {Object} producto - Datos del producto en formato frontend
   * @returns {Promise<Object>} Producto creado con ID asignado
   */
  async create(producto) {
    const dto = this.mapToCreateDto(producto)
    const response = await api.post('/ProductosServicios', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: update
   * ===========================================================================
   *
   * Actualiza un producto/servicio existente.
   *
   * ENDPOINT: PUT /api/ProductosServicios/{id}
   *
   * @param {number} id - ID del producto
   * @param {Object} producto - Datos actualizados
   * @returns {Promise<Object>} Producto actualizado
   */
  async update(id, producto) {
    const dto = this.mapToUpdateDto(producto)
    const response = await api.put(`/ProductosServicios/${id}`, dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: toggleActive
   * ===========================================================================
   *
   * Activa o desactiva un producto/servicio.
   *
   * ENDPOINT: PATCH /api/ProductosServicios/{id}/toggle-active
   *
   * ¿PARA QUÉ DESACTIVAR?
   * ---------------------
   * - Producto descontinuado que ya no se vende
   * - Servicio temporalmente no disponible
   * - NO eliminar porque puede haber facturas históricas
   *
   * Un producto desactivado:
   * - NO aparece en búsquedas al crear factura
   * - SÍ aparece en el historial de facturas donde fue usado
   *
   * PATCH vs PUT:
   * - PUT: Reemplaza TODO el recurso
   * - PATCH: Modifica PARCIALMENTE el recurso (solo el campo activo)
   *
   * @param {number} id - ID del producto
   * @returns {Promise<Object>} Estado actualizado
   */
  async toggleActive(id) {
    const response = await api.patch(`/ProductosServicios/${id}/toggle-active`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: mapToCreateDto
   * ===========================================================================
   *
   * Transforma datos del frontend al DTO de creación del backend.
   *
   * ESTRUCTURA FRONTEND:
   * {
   *   codigo: "SKU001",
   *   nombre: "Laptop HP",
   *   descripcion: "Laptop HP 15 pulgadas...",
   *   precioVenta: "599.99",  // Puede venir como string
   *   unidadMedidaId: "59",   // ID como string
   *   tipoItemId: "1"         // 1=Bien, 2=Servicio
   * }
   *
   * ESTRUCTURA BACKEND (CreateProductoServicioDto):
   * {
   *   codigo: "SKU001",
   *   nombre: "Laptop HP",
   *   descripcion: "Laptop HP 15 pulgadas...",
   *   precioVenta: 599.99,      // Número decimal
   *   catUnidadMedidaId: 59,    // ID numérico (FK a catálogo)
   *   catTipoItemId: 1          // ID numérico (FK a catálogo)
   * }
   *
   * @param {Object} producto - Producto en formato frontend
   * @returns {Object} CreateProductoServicioDto
   */
  mapToCreateDto(producto) {
    const dto = {
      // Código interno del producto (SKU)
      // Si está vacío, el backend auto-genera PROD-XXXXX o SERV-XXXXX según tipo
      codigo: (producto.codigo || '').trim(),

      // Nombre corto
      nombre: producto.nombre || '',

      // Descripción larga (aparece en la factura)
      // null si está vacío (no undefined)
      descripcion: producto.descripcion || null,

      // Precio de venta - parseFloat convierte string a número decimal
      // Si falla la conversión, usa 0
      // NOTA: Siempre se guarda SIN IVA en la BD
      precioVenta: parseFloat(producto.precioVenta) || 0,

      // ID de unidad de medida (FK a tabla cat_unidades_medida)
      // parseInt convierte string a entero
      // 59 = "Unidad" es el valor por defecto
      catUnidadMedidaId: parseInt(producto.unidadMedidaId) || 59,

      // ID de tipo de ítem (FK a tabla cat_tipos_item)
      // 1 = "Bien" es el valor por defecto
      catTipoItemId: parseInt(producto.tipoItemId) || 1,

      // ==========================================
      // CAMPOS ADICIONALES
      // ==========================================

      // Código de barras / SKU alternativo
      codigoBarras: producto.codigoBarras || null,

      // Clasificación del producto (FK a categorías)
      categoriaId: producto.categoriaId ? parseInt(producto.categoriaId) : null,

      // Marca del producto (FK a marcas)
      marcaId: producto.marcaId ? parseInt(producto.marcaId) : null,

      // Costo de adquisición (para calcular margen)
      precioCosto: producto.precioCosto ? parseFloat(producto.precioCosto) : null,

      // Tipo de impuesto: 1=Gravado (IVA), 2=Exento, 3=NoSujeto
      // IMPORTANTE: Determina cómo se factura este producto
      tipoImpuesto: parseInt(producto.tipoImpuesto) || 1,

      // Porcentaje de IVA (ej: 13) - Solo aplica si tipoImpuesto = 1 (Gravado)
      porcentajeIVA: producto.porcentajeIVA !== undefined ? parseFloat(producto.porcentajeIVA) : 13,

      // Flags: indica si el precio incluye IVA
      precioIncluyeIva: producto.precioIncluyeIva === true,
      costoIncluyeIva: producto.costoIncluyeIva === true,

      // Control de inventario
      stockMinimo: producto.stockMinimo ? parseFloat(producto.stockMinimo) : null,
      stockMaximo: producto.stockMaximo ? parseFloat(producto.stockMaximo) : null,
      puntoReorden: producto.puntoReorden ? parseFloat(producto.puntoReorden) : null,

      // Permitir vender sin existencia (servicios o productos especiales)
      permiteVentaSinStock: producto.permiteVentaSinStock !== undefined ? producto.permiteVentaSinStock : null,

      // Multi-sucursal (M:N)
      accesoTodasSucursales: producto.accesoTodasSucursales || false,
      sucursalIds: Array.isArray(producto.sucursalIds) && producto.sucursalIds.length > 0
        ? producto.sucursalIds.map(id => parseInt(id))
        : null,

      // F2: clasificacion contable + activo fijo. Solo se envia el bloque
      // contable cuando aplica (MobiliarioEquipo); para Ventas/Insumos se
      // ignoran los campos opcionales (el backend acepta null).
      tipoInventario: producto.tipoInventario != null ? parseInt(producto.tipoInventario) : 0,
      fechaAdquisicion: producto.tipoInventario === 1 && producto.fechaAdquisicion
        ? producto.fechaAdquisicion
        : null,
      aniosVidaUtil: producto.tipoInventario === 1 && producto.aniosVidaUtil
        ? parseInt(producto.aniosVidaUtil)
        : null,
      valorActual: producto.tipoInventario === 1 && producto.valorActual != null
        ? parseFloat(producto.valorActual)
        : null,
      valorResidual: producto.tipoInventario === 1 && producto.valorResidual != null
        ? parseFloat(producto.valorResidual)
        : null
    }

    // Agregar emisorId si se proporciona (para vincular con un emisor específico)
    if (producto.emisorId) {
      dto.emisorId = parseInt(producto.emisorId)
    }

    return dto
  },

  /**
   * ===========================================================================
   * MÉTODO: mapToUpdateDto
   * ===========================================================================
   *
   * Transforma datos del frontend al DTO de actualización del backend.
   * Similar a mapToCreateDto pero incluye campo 'activo'.
   *
   * @param {Object} producto - Producto en formato frontend
   * @returns {Object} UpdateProductoServicioDto
   */
  mapToUpdateDto(producto) {
    return {
      codigo: producto.codigo || '',
      nombre: producto.nombre || '',
      descripcion: producto.descripcion || null,
      precioVenta: parseFloat(producto.precioVenta) || 0,
      // !== false asegura que sea true por defecto
      activo: producto.activo !== false,
      catUnidadMedidaId: parseInt(producto.unidadMedidaId) || 59,
      catTipoItemId: parseInt(producto.tipoItemId) || 1,

      // ==========================================
      // CAMPOS ADICIONALES
      // ==========================================
      codigoBarras: producto.codigoBarras || null,
      categoriaId: producto.categoriaId ? parseInt(producto.categoriaId) : null,
      marcaId: producto.marcaId ? parseInt(producto.marcaId) : null,
      precioCosto: producto.precioCosto ? parseFloat(producto.precioCosto) : null,
      tipoImpuesto: parseInt(producto.tipoImpuesto) || 1,
      porcentajeIVA: producto.porcentajeIVA !== undefined ? parseFloat(producto.porcentajeIVA) : 13,
      precioIncluyeIva: producto.precioIncluyeIva === true,
      costoIncluyeIva: producto.costoIncluyeIva === true,
      stockMinimo: producto.stockMinimo ? parseFloat(producto.stockMinimo) : null,
      stockMaximo: producto.stockMaximo ? parseFloat(producto.stockMaximo) : null,
      puntoReorden: producto.puntoReorden ? parseFloat(producto.puntoReorden) : null,
      permiteVentaSinStock: producto.permiteVentaSinStock !== undefined ? producto.permiteVentaSinStock : null,

      // Multi-sucursal (M:N)
      accesoTodasSucursales: producto.accesoTodasSucursales || false,
      sucursalIds: Array.isArray(producto.sucursalIds) && producto.sucursalIds.length > 0
        ? producto.sucursalIds.map(id => parseInt(id))
        : null,

      // F2: clasificacion contable + activo fijo (mismo criterio que CreateDto).
      tipoInventario: producto.tipoInventario != null ? parseInt(producto.tipoInventario) : 0,
      fechaAdquisicion: producto.tipoInventario === 1 && producto.fechaAdquisicion
        ? producto.fechaAdquisicion
        : null,
      aniosVidaUtil: producto.tipoInventario === 1 && producto.aniosVidaUtil
        ? parseInt(producto.aniosVidaUtil)
        : null,
      valorActual: producto.tipoInventario === 1 && producto.valorActual != null
        ? parseFloat(producto.valorActual)
        : null,
      valorResidual: producto.tipoInventario === 1 && producto.valorResidual != null
        ? parseFloat(producto.valorResidual)
        : null
    }
  },

  /**
   * ===========================================================================
   * MÉTODO: mapFromDto
   * ===========================================================================
   *
   * Transforma la respuesta del backend al formato del frontend.
   *
   * EL BACKEND PUEDE RETORNAR DOS FORMATOS:
   * 1. ProductoServicioDto (completo) - getById
   *    unidadMedida: { id: 59, codigo: "59", valor: "Unidad" }
   *
   * 2. ProductoServicioListDto (simplificado) - getAll, search
   *    unidadMedida: "Unidad" (string directo)
   *
   * Este método maneja ambos casos.
   *
   * @param {Object} dto - DTO del backend (puede ser completo o de lista)
   * @returns {Object} Producto en formato frontend
   */
  mapFromDto(dto) {
    // Detectar si es el DTO de lista (strings) o el completo (objetos)
    // typeof verifica el tipo de dato
    const esListDto = typeof dto.tipoItem === 'string' || typeof dto.unidadMedida === 'string'

    return {
      // ID del producto
      id: dto.id,

      // Datos básicos
      codigo: dto.codigo || '',
      nombre: dto.nombre || '',
      descripcion: dto.descripcion || '',
      precioVenta: dto.precioVenta || 0,
      activo: dto.activo !== false,
      fechaCreacion: dto.fechaCreacion,

      // Unidad de medida - depende del tipo de DTO
      // Si es ListDto, unidadMedida es string, no tenemos el objeto completo
      unidadMedida: esListDto ? null : (dto.unidadMedida || null),
      unidadMedidaId: esListDto ? null : (dto.unidadMedida?.id || null),
      unidadMedidaDesc: esListDto ? dto.unidadMedida : (dto.unidadMedida?.valor || 'Unidad'),

      // Tipo de ítem - mismo manejo que unidad de medida
      tipoItem: esListDto ? null : (dto.tipoItem || null),
      tipoItemId: esListDto ? null : (dto.tipoItem?.id || null),
      tipoItemDesc: esListDto ? dto.tipoItem : (dto.tipoItem?.valor || 'Bien'),

      // Datos del emisor propietario del producto
      emisorId: dto.emisorId,
      emisorNombre: dto.emisorNombre || '',

      // ==========================================
      // CAMPOS ADICIONALES
      // ==========================================
      codigoBarras: dto.codigoBarras || null,
      categoriaId: dto.categoriaId || null,
      marcaId: dto.marcaId || null,
      precioCosto: dto.precioCosto || null,

      // Tipo de impuesto: 1=Gravado, 2=Exento, 3=NoSujeto
      // IMPORTANTE: Determina cómo se factura este producto
      tipoImpuesto: dto.tipoImpuesto || 1,

      // Porcentaje de IVA (por defecto 13%)
      porcentajeIVA: dto.porcentajeIVA !== undefined ? dto.porcentajeIVA : 13,

      // Flags: indica si el precio incluye IVA
      precioIncluyeIva: dto.precioIncluyeIva === true,
      costoIncluyeIva: dto.costoIncluyeIva === true,

      // Control de inventario
      stockMinimo: dto.stockMinimo || null,
      stockMaximo: dto.stockMaximo || null,
      puntoReorden: dto.puntoReorden || null,
      permiteVentaSinStock: dto.permiteVentaSinStock !== undefined ? dto.permiteVentaSinStock : null,

      // Multi-sucursal (M:N)
      accesoTodasSucursales: dto.accesoTodasSucursales || false,
      sucursales: dto.sucursales || [],
      sucursalIds: (dto.sucursales || []).map(s => s.id),

      // F2: clasificacion contable y campos de activo fijo.
      // tipoInventario: 0=Ventas (default), 1=MobiliarioEquipo, 2=Insumos.
      tipoInventario: dto.tipoInventario != null ? dto.tipoInventario : 0,
      fechaAdquisicion: dto.fechaAdquisicion ? dto.fechaAdquisicion.substring(0, 10) : null,
      aniosVidaUtil: dto.aniosVidaUtil != null ? dto.aniosVidaUtil : null,
      valorActual: dto.valorActual != null ? dto.valorActual : null,
      valorResidual: dto.valorResidual != null ? dto.valorResidual : null
    }
  },

  /**
   * ===========================================================================
   * MÉTODO: mapListFromDto
   * ===========================================================================
   *
   * Transforma una lista de DTOs del backend al formato frontend.
   *
   * @param {Array} dtos - Lista de DTOs del backend
   * @returns {Array} Lista de productos en formato frontend
   */
  mapListFromDto(dtos) {
    return dtos.map(dto => this.mapFromDto(dto))
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import productosServiciosService from '@/services/productosServicios.service'
 *
 * // Buscar productos para autocomplete
 * const productos = await productosServiciosService.search("laptop")
 *
 * // Crear nuevo producto
 * await productosServiciosService.create({
 *   codigo: 'SKU001',
 *   nombre: 'Laptop HP',
 *   precioVenta: 599.99,
 *   unidadMedidaId: 59,
 *   tipoItemId: 1
 * })
 *
 * // Desactivar producto
 * await productosServiciosService.toggleActive(productId)
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/views/configuracion/ProductosServiciosView.vue → CRUD completo
 * - src/components/emision/CuerpoDocumento.vue → Autocomplete al agregar líneas
 */
export default productosServiciosService
